import React, { useState } from "react";
import { Plus, Heart, Check, Loader2, AlertCircle } from "lucide-react";
import { usePartyStore } from "../../stores/partyStore";
import { useAuthStore } from "../../stores/authStore";
import { addSongToQueue } from "../../lib/firebase";
import { useFavorites } from "../../hooks/useFavorites";
import type { YouTubeSearchResult } from "../../types";

interface SearchResultListProps {
  results: YouTubeSearchResult[];
  loading: boolean;
}

export const SearchResultList: React.FC<SearchResultListProps> = ({
  results,
  loading,
}) => {
  const { currentParty, setActiveTab } = usePartyStore();
  const { user } = useAuthStore();
  const { isFavorite, addFavorite, removeFavorite } = useFavorites();

  const [addingId, setAddingId] = useState<string | null>(null);
  const [addedSuccessId, setAddedSuccessId] = useState<string | null>(null);

  const handleAddToQueue = async (item: YouTubeSearchResult) => {
    if (!currentParty || !user) return;
    const videoId = item.id.videoId;
    setAddingId(videoId);

    try {
      await addSongToQueue(currentParty.id, item, user);

      setAddedSuccessId(videoId);
      setTimeout(() => {
        setAddedSuccessId(null);
        setActiveTab("queue");
      }, 800);
    } catch (err: any) {
      alert(`Could not add song: ${err.message}`);
    } finally {
      setAddingId(null);
    }
  };

  const toggleFavorite = (item: YouTubeSearchResult) => {
    const videoId = item.id.videoId;
    if (isFavorite(videoId)) {
      removeFavorite(videoId);
    } else {
      addFavorite({
        videoId,
        title: item.snippet.title,
        channelTitle: item.snippet.channelTitle,
        thumbnailUrl: item.snippet.thumbnails.default?.url || "",
        embeddable: item.status?.embeddable ?? true,
      });
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center space-y-3">
        <Loader2 className="w-8 h-8 mx-auto text-primary animate-spin" />
        <p className="text-xs text-text-muted">Searching YouTube for karaoke tracks...</p>
      </div>
    );
  }

  if (results.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 pt-2">
      <h3 className="text-sm font-bold text-text-muted px-1">
        Search Results ({results.length})
      </h3>

      <div className="grid grid-cols-1 gap-3">
        {results.map((item) => {
          const videoId = item.id.videoId;
          const isEmbeddable = item.status?.embeddable ?? true;
          const isFav = isFavorite(videoId);
          const isAdding = addingId === videoId;
          const isAdded = addedSuccessId === videoId;

          return (
            <div
              key={videoId}
              className="bg-card p-3 sm:p-4 rounded-2xl border border-border hover:border-primary/50 transition-all flex items-center gap-3 sm:gap-4 shadow-md group"
            >
              {/* Thumbnail */}
              <div className="relative w-20 h-14 sm:w-24 sm:h-16 rounded-xl overflow-hidden bg-background shrink-0 shadow-sm">
                <img
                  src={
                    item.snippet.thumbnails.medium?.url ||
                    item.snippet.thumbnails.default?.url ||
                    ""
                  }
                  alt={item.snippet.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {!isEmbeddable && (
                  <span
                    className="absolute top-1 right-1 bg-red-600/90 text-white text-[8px] font-bold px-1 rounded flex items-center gap-0.5"
                    title="External playback required"
                  >
                    <AlertCircle className="w-2.5 h-2.5" /> EXT
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="min-w-0 flex-1 space-y-0.5">
                <h4 className="text-xs sm:text-sm font-bold text-text-main line-clamp-2 leading-tight">
                  {item.snippet.title}
                </h4>
                <p className="text-[11px] text-text-muted truncate">
                  {item.snippet.channelTitle}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                {/* Favorite Button */}
                <button
                  type="button"
                  onClick={() => toggleFavorite(item)}
                  className={`p-2.5 rounded-xl border transition-colors cursor-pointer ${
                    isFav
                      ? "bg-red-500/10 border-red-500/40 text-red-500"
                      : "bg-background/80 border-border text-text-muted hover:text-red-400"
                  }`}
                  title={isFav ? "Remove from Favorites" : "Add to Favorites"}
                >
                  <Heart className={`w-4 h-4 ${isFav ? "fill-red-500" : ""}`} />
                </button>

                {/* Add to Queue Button */}
                <button
                  type="button"
                  onClick={() => handleAddToQueue(item)}
                  disabled={isAdding || isAdded}
                  className={`py-2 px-3 sm:px-4 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all shadow-md cursor-pointer ${
                    isAdded
                      ? "bg-green-600 text-white"
                      : "bg-primary hover:bg-primary-hover text-white disabled:opacity-50"
                  }`}
                  title="Add Song to Party Queue"
                >
                  {isAdding ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isAdded ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span className="hidden sm:inline">Added!</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span className="hidden sm:inline">Add to Queue</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
